import { useEffect, useState } from 'react';
import axios from 'axios';
import config from '../config';

const sameId = (a, b) => String(a || '') === String(b || '');

const userInCall = (call, userId) =>
  (call?.participantIds || []).some((id) => sameId(id, userId));

/**
 * Group calls this member can still join. Updates when a call starts,
 * when someone joins or leaves, and when the call ends.
 */
export default function useActiveGroupCalls(user, socket) {
  const userId = user?.id || user?._id;
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    axios
      .get(`${config.apiUrl}/api/calls/my-active-group-calls`, { params: { userId } })
      .then((res) => {
        if (!cancelled) setCalls(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setCalls([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!socket || !userId) return undefined;

    const refresh = () => {
      axios
        .get(`${config.apiUrl}/api/calls/my-active-group-calls`, { params: { userId } })
        .then((res) => setCalls(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    };

    const upsert = (data) => {
      if (!data?.groupCallId) return;
      setCalls((prev) => {
        const next = prev.filter((call) => !sameId(call.groupCallId, data.groupCallId));
        next.push(data);
        return next;
      });
    };

    const remove = (data) => {
      if (!data?.groupCallId && !data?.groupId) return;
      setCalls((prev) => prev.filter((call) => {
        if (data.groupCallId && sameId(call.groupCallId, data.groupCallId)) return false;
        if (!data.groupCallId && data.groupId && sameId(call.groupId, data.groupId)) return false;
        return true;
      }));
    };

    socket.on('groupCallActive', upsert);
    socket.on('groupCallInactive', remove);
    socket.on('connect', refresh);
    return () => {
      socket.off('groupCallActive', upsert);
      socket.off('groupCallInactive', remove);
      socket.off('connect', refresh);
    };
  }, [socket, userId]);

  return calls.filter((call) => call?.groupCallId && !userInCall(call, userId));
}
